import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GlobalConstants } from './global-constants';
import { AuthService } from './services/auth.service';
import { CustomerLoginDialogComponent } from './header/customer-login.component';

declare var $: any;

// The booking page has its own inline register/login section rather than relying
// on the header's popup dialog. Scroll/focus there instead of stacking a modal on top.
const BOOKING_AUTH_SECTION_ID = 'bookingAuthSection';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  // Guards against opening a second login dialog when several requests 401 at once.
  private loginDialogOpen = false;

  constructor(private authService: AuthService, private dialog: MatDialog, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiRequest = req.url.startsWith(GlobalConstants.apiURL);
    const token = isApiRequest ? this.authService.getToken() : null;

    const authReq = token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isApiRequest && error.status === 401) {
          this.authService.clearSession();
          this.promptLogin();
        }
        return throwError(error);
      })
    );
  }

  // A 401 means the current action needs a logged-in customer. Rather than booting
  // the user back to the home page (and losing whatever they were filling in, e.g.
  // a booking form), prompt them to log in or register in place so they can continue.
  private promptLogin(): void {
    // Some call sites (e.g. booking submission) never hide the preloader on
    // error, so it would otherwise keep spinning behind the login dialog.
    $('.preloader').hide();

    if (this.router.url.startsWith('/booking') && this.focusBookingAuthSection()) {
      return;
    }

    if (this.loginDialogOpen) {
      return;
    }
    this.loginDialogOpen = true;
    const dialogRef = this.dialog.open(CustomerLoginDialogComponent);
    dialogRef.afterClosed().subscribe(() => {
      this.loginDialogOpen = false;
    });
  }

  // Returns true if the booking page's inline login/register section was found and focused.
  private focusBookingAuthSection(): boolean {
    const section = document.getElementById(BOOKING_AUTH_SECTION_ID);
    if (!section) {
      return false;
    }
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = section.querySelector('input') as HTMLElement | null;
    input?.focus();
    return true;
  }
}
